import { useEffect, useRef, useSyncExternalStore } from "react"

import {
  getSofiaPageSummaryServerSnapshot,
  getSofiaPageSummarySnapshot,
  publishSofiaPageSummary,
  registerSofiaPageSummary,
  subscribeSofiaPageSummary,
  type SofiaPageSummary,
} from "@/lib/covi/sofiaPageSummary"

/**
 * Hand this page's Sofia summary to the Sofia panel, which presents it in the
 * chat.
 *
 * Pass `null` while the page has no summary (still loading, feature flag off,
 * empty body) — nothing is then presented. The slot is claimed for the lifetime
 * of the calling component and released on unmount, so leaving the page always
 * clears it.
 */
export function usePublishSofiaPageSummary(
  summary: SofiaPageSummary | null,
): void {
  const registrationRef = useRef<symbol | null>(null)

  useEffect(() => {
    const { registration, unregister } = registerSofiaPageSummary()
    registrationRef.current = registration
    return () => {
      registrationRef.current = null
      unregister()
    }
  }, [])

  // Publishing in an effect (not during render) keeps the store write out of
  // the render pass, so a subscriber re-rendering can never feed back into the
  // publishing component's render.
  useEffect(() => {
    const registration = registrationRef.current
    if (!registration) {
      return
    }
    publishSofiaPageSummary(registration, summary)
  }, [summary])
}

/** The summary the current page published, or null when there is none. */
export function useSofiaPageSummary(): SofiaPageSummary | null {
  return useSyncExternalStore(
    subscribeSofiaPageSummary,
    getSofiaPageSummarySnapshot,
    getSofiaPageSummaryServerSnapshot,
  )
}
